import { clsx } from 'clsx'
import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { ModalProvider } from '../../providers'
import { getComposerFileStore, useWebchatStore } from '../../stores'
import { webchatClasses } from '../../styles/classes'
import { CloudUpload } from 'lucide-react'
import { ScopedClient } from '../../types'

type Props = ComponentProps<'div'> & {
  connected?: boolean
  uploadFile?: ScopedClient['uploadFile']
  allowFileUpload?: boolean
  storageKey?: string
  conversationId?: string
}

export const Container = ({
  children,
  className,
  uploadFile,
  allowFileUpload,
  storageKey,
  conversationId,
  ...props
}: Props) => {
  const { container } = webchatClasses

  const setMessageContainerRef = useWebchatStore((s) => s.setMessageContainerRef)

  const useComposerFileStore = getComposerFileStore(conversationId || 'default', storageKey)
  const uploadComposerFile = useComposerFileStore((state) => state.uploadFile)
  const [dragDepth, setDragDepth] = useState(0)

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setMessageContainerRef(ref)
  }, [ref, setMessageContainerRef])

  const onDrop = useCallback(
    (files: File[]) => {
      if (!uploadFile) return
      files.forEach((file) => uploadComposerFile(file, uploadFile))
      setDragDepth(0)
    },
    [uploadComposerFile, uploadFile]
  )

  const { getRootProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
    disabled: !(allowFileUpload && uploadFile),
    onDragEnter: () => setDragDepth((prev) => prev + 1),
    onDragLeave: () => setDragDepth((prev) => prev - 1),
  })

  return (
    <div {...props} className={clsx(container.className, className)} {...getRootProps()} ref={ref}>
      <div data-hover={dragDepth > 0} {...container.dropzone.overlay}>
        Drop files to upload
        <CloudUpload style={{ width: '50px' }} />
      </div>
      <ModalProvider>{children}</ModalProvider>
    </div>
  )
}

Container.displayName = 'Container'
